import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { Skill } from "@cohesive/api-client";
import {
  Icon,
  INSERT_SKILL_COMMAND,
  INSERT_TEXT_COMMAND,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ScrollText, X } from "lucide-react";
import { KeyboardEvent } from "react";

interface SkillsMenuProps {
  iconClassName?: string;
  triggerButtonClassName?: string;
  skillsEnabled: boolean;
  filteredSkills: Skill[];
  skillsFilter: string;
  setSkillsFilter: (filter: string) => void;
  skillsMenuOpen: boolean;
  setSkillsMenuOpen: (open: boolean) => void;
  mentionMode: "@" | "/" | null;
  setMentionMode: (mode: "@" | "/" | null) => void;
  focusTextarea: () => void;
  leftBoundary: number;
  containerRect?: DOMRect | null;
}

export const SkillsMenu: React.FC<SkillsMenuProps> = ({
  iconClassName,
  triggerButtonClassName,
  skillsEnabled,
  filteredSkills,
  skillsFilter,
  setSkillsFilter,
  skillsMenuOpen,
  setSkillsMenuOpen,
  mentionMode,
  setMentionMode,
  focusTextarea,
  leftBoundary,
}) => {
  const [editor] = useLexicalComposerContext();

  // Typing "/" in the composer opens this popover and moves keyboard focus
  // into the search field, so the next keystroke lands here instead of in the
  // message. A space on an empty query means the user is writing prose
  // ("/ path", "half / half") rather than picking a skill, so dismiss the menu
  // and replay the space into the composer, leaving "/ " as plain text.
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const openedBySlash = mentionMode === "/";
    if (event.key !== " " || skillsFilter !== "" || !openedBySlash) return;

    event.preventDefault();
    setSkillsMenuOpen(false);
    setMentionMode(null);
    setSkillsFilter("");

    // Defer until the popover has torn down so its focus management doesn't
    // pull focus back off the composer before the space is inserted.
    setTimeout(() => {
      focusTextarea();
      editor.dispatchCommand(INSERT_TEXT_COMMAND, { text: " " });
    }, 0);
  };

  return (
    <div className="z-9 order-2">
      <Popover
        open={skillsMenuOpen}
        onOpenChange={(open) => {
          setSkillsMenuOpen(open);
          if (!open) {
            focusTextarea();
          }
        }}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground hover:text-foreground transition-colors",
                  triggerButtonClassName,
                )}
              >
                <ScrollText className={iconClassName} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Add skill '/'</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          sideOffset={8}
          collisionPadding={{ left: leftBoundary + 20 }}
          // Menu canon (matches DropdownMenuContent): p-1 rounded-md, compact
          // width — same register as the @ add menu and the canvas menus.
          className="w-[260px] min-w-[260px] p-1 rounded-md"
          onCloseAutoFocus={(e) => {
            // Prevent popover from auto-focusing the trigger button on close.
            // This ensures focus returns to the textarea input instead.
            e.preventDefault();
          }}
        >
          <Command
            className="bg-transparent rounded-none overflow-hidden w-full"
            shouldFilter={false}
          >
            <CommandList className="max-h-[min(60vh,420px)] overflow-y-auto">
              <div className="relative">
                <CommandInput
                  placeholder="Search skills…"
                  value={skillsFilter}
                  onValueChange={setSkillsFilter}
                  onKeyDown={handleSearchKeyDown}
                  className="h-9 pr-8"
                />
                {skillsFilter && (
                  <button
                    type="button"
                    aria-label="Clear skills search"
                    onClick={() => setSkillsFilter("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                  >
                    <X className="size-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
              <CommandGroup className="p-0 pt-1">
                {skillsEnabled &&
                  filteredSkills.map((skill) => (
                    <CommandItem
                      key={`skill-${skill.id}`}
                      onSelect={() => {
                        editor.dispatchCommand(INSERT_SKILL_COMMAND, {
                          id: skill.id,
                          name: skill.name,
                        });
                        analytics.trackSkillSelected({ skillId: skill.id });
                        setSkillsMenuOpen(false);
                        setMentionMode(null);
                        setTimeout(() => focusTextarea(), 0);
                      }}
                    >
                      <span className="flex size-4 shrink-0 items-center justify-center">
                        {skill.icon && skill.icon.type ? (
                          <Icon icon={skill.icon} size={16} />
                        ) : (
                          <ScrollText className="size-4" />
                        )}
                      </span>
                      <span className="truncate">{skill.name}</span>
                    </CommandItem>
                  ))}
                {filteredSkills.length === 0 && (
                  <p className="px-2 py-1.5 text-sm text-muted-foreground">
                    No matching skills.
                  </p>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
